/**
 * Redux slice for handling user feedback submissions.
 * Uses Firebase Firestore to store feedback data.
 * Manages state for sending status, success, and error messages.
 * Includes async thunk for submitting feedback.
 * Has actions to reset feedback state.
 * Fields stored: message, email, name, userId, plan, route, userAgent, appVersion, status, createdAt.
 */

//redux toolkit
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

//firebase
import { db } from "../../app/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

//utils
import { getAppVersion } from "../../utils/getAppVersion";

export const submitFeedback = createAsyncThunk(
  "feedback/submit",
  async ({
    message,
    email,
    name,
    subject,
    subjectLabel,
    userId,
    plan,
    route,
    userAgent,
    appVersion,
  }, { rejectWithValue }) => {
    try {
      const payload = {
        message: (message || "").trim(),
        email: (email || "").trim(),
        name: (name || "").trim(),
        subject: subject || "general",
        subjectLabel: (subjectLabel || "").trim(),
        userId: userId || null,
        plan: plan || "unknown",
        route: route || "",
        userAgent: userAgent || "",
        appVersion: getAppVersion(),
        status: "new",
        createdAt: serverTimestamp(),
      };
      const docId = `${email}_${Date.now()}`;
        await setDoc(doc(db, "feedback", docId), payload);
      return true;
    } catch (e) {
      return rejectWithValue(e.message || "Failed to send feedback");
    }
  }
);

const feedbackSlice = createSlice({
  name: "feedback",
  initialState: { sending: false, error: null, success: false },
  reducers: {
    resetFeedbackState: (s) => { s.sending = false; s.error = null; s.success = false; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitFeedback.pending, (s) => { s.sending = true; s.error = null; s.success = false; })
      .addCase(submitFeedback.fulfilled, (s) => { s.sending = false; s.success = true; })
      .addCase(submitFeedback.rejected, (s, a) => {
        s.sending = false;
        s.error = a.payload || "Could not send feedback. Please try again.";
      });

  }
});

export const { resetFeedbackState } = feedbackSlice.actions;
export default feedbackSlice.reducer;
